import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { InventoryBatch } from '../../types';
import StatusChip from '../common/StatusChip';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatCurrency } from '../../utils/currency';
import { formatDate } from '../../utils/date';
import { Tune } from '@mui/icons-material';

interface BatchListViewProps {
  batches: InventoryBatch[];
  loading: boolean;
  onRefresh: () => void;
  onBatchSelect?: (batch: InventoryBatch) => void;
}

const BatchListView: React.FC<BatchListViewProps> = ({
  batches,
  loading,
  onRefresh,
  onBatchSelect,
}) => {
  const { t, language } = useLanguage();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (batches.length === 0) {
    return (
      <Alert severity="info">
        {t('inventory.noBatches')}
      </Alert>
    );
  }

  const getBatchStatus = (batch: InventoryBatch) => {
    if (batch.empty) {
      return { label: t('inventory.empty'), color: 'default' as const };
    }
    if (batch.expired) {
      return { label: t('inventory.expired'), color: 'error' as const };
    }
    if (batch.expiringSoon) {
      return { label: t('inventory.expiringSoon'), color: 'warning' as const };
    }
    return { label: t('inventory.active'), color: 'success' as const };
  };

  // Calculate totals
  const totalQuantity = batches.reduce((sum, batch) => sum + batch.remainingQuantity, 0);
  const totalValue = batches.reduce((sum, batch) => sum + (batch.remainingQuantity * (batch.priceGross ?? batch.price)), 0);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('inventory.batchDetails')} ({batches.length})
      </Typography>
      {onBatchSelect && <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{t('inventory.selectBatchToAdjust')}</Typography>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('inventory.batchNumber')}</TableCell>
              <TableCell align="right">{t('inventory.quantity')}</TableCell>
              <TableCell align="right">{t('inventory.price')}</TableCell>
              <TableCell align="right">{t('inventory.batchTotal')}</TableCell>
              <TableCell>{t('inventory.productionDate')}</TableCell>
              <TableCell>{t('inventory.expirationDate')}</TableCell>
              <TableCell>{t('inventory.status')}</TableCell>
              {onBatchSelect && <TableCell align="right">{t('common.actions')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {batches.map((batch) => {
              const status = getBatchStatus(batch);
              return (
                <TableRow key={batch.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {batch.batchNumber || t('inventory.noBatchNumber')}
                    </Typography>
                    {batch.notes && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {batch.notes}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography 
                      variant="body2" 
                      color={batch.remainingQuantity === 0 ? 'text.secondary' : 'text.primary'}
                      fontWeight="medium"
                    >
                      {batch.remainingQuantity}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(batch.priceGross ?? batch.price)}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(batch.remainingQuantity * (batch.priceGross ?? batch.price))}
                    </Typography>
                  </TableCell>
                  <TableCell>
                  {formatDate(batch.productionDate, language)}
                  </TableCell>
                  <TableCell>
                  {formatDate(batch.expirationDate, language)}
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      label={status.label}
                      color={status.color}
                    />
                  </TableCell>
                  {onBatchSelect && <TableCell align="right"><Button size="small" startIcon={<Tune fontSize="small" />} onClick={() => onBatchSelect(batch)}>{t('inventory.adjustStock')}</Button></TableCell>}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary */}
      <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" gutterBottom>
          {t('inventory.batchSummary')}
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
          <Typography variant="body2" color="text.secondary">
            {t('inventory.totalBatches')}: {batches.length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('inventory.activeBatches')}: {batches.filter(b => !b.empty).length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('inventory.expiredBatches')}: {batches.filter(b => b.expired).length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('inventory.expiringSoonBatches')}: {batches.filter(b => b.expiringSoon).length}
          </Typography>
        </Box>
        
        {/* Financial Summary */}
        <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('inventory.financialSummary')}
          </Typography>
          <Box display="flex" gap={4} flexWrap="wrap">
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('inventory.totalQuantity')}
              </Typography>
              <Typography variant="h6" color="primary">
                {totalQuantity} {t('inventory.units')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('inventory.totalValue')}
              </Typography>
              <Typography variant="h6" color="success.main">
                {formatCurrency(totalValue)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BatchListView;
